/*
 * <brand-list> custom element (VIS-32).
 *
 * Liquid can read at most 250 metaobject entries per render and the store has
 * 400+ brands, so the section ships as an empty shell and this element owns
 * the data: it fetches every page of the JSON endpoint rendered by
 * templates/page.brand-data.liquid (the `brand-data` alternate view on the
 * current page URL), sorts the brands alphabetically, groups them by first
 * letter (digits and anything else non-alphabetic under "0-9"), renders the
 * letter groups and switches the matching index letters from their disabled
 * state to in-page anchors.
 */

// Mirrors store-locator's HYDRATION_CONCURRENCY: cap parallel fetches so a
// large catalog doesn't fire every page request at once.
const FETCH_CONCURRENCY = 4;
const DIGIT_KEY = '0-9';

class BrandList extends HTMLElement {
  connectedCallback() {
    this.groupsContainer = this.querySelector('[data-brand-list-groups]');
    this.indexLetters = Array.from(this.querySelectorAll('[data-letter]'));
    this.view = this.dataset.view || 'brand-data';
    // Section-setting toggles (VIS-509). Default on when the attribute is
    // absent; only the literal "false" disables.
    this.filterNoCollection = this.dataset.filterNoCollection !== 'false';
    this.keepWithProducts = this.dataset.keepWithProducts !== 'false';
    this.vendorLink = this.dataset.vendorLink !== 'false';
    void this.#load();
  }

  async #load() {
    if (!this.groupsContainer) return;

    try {
      const first = await this.#fetchPage(null, null);
      if (!first) throw new Error('brand-list: empty first page');

      const brands = [...(first.brands || [])];
      const pageNumbers = [];
      for (let n = 2; n <= (first.pages || 1); n += 1) pageNumbers.push(n);

      for (let i = 0; i < pageNumbers.length; i += FETCH_CONCURRENCY) {
        const batch = pageNumbers.slice(i, i + FETCH_CONCURRENCY);
        const results = await Promise.all(
          batch.map((page) => this.#fetchPage(page, first.page_param)),
        );
        for (const result of results) {
          if (result) brands.push(...(result.brands || []));
        }
      }

      this.#render(brands);
    } catch (error) {
      // Leave the loading message in place; there is no server-rendered
      // fallback by design.
      console.error(error);
    }
  }

  async #fetchPage(page, pageParam) {
    const url = new URL(window.location.pathname, window.location.origin);
    url.searchParams.set('view', this.view);
    // Paginating metaobject values uses an auto-assigned page param (e.g.
    // `page_b437…`) reported by the endpoint as `page_param` — the plain
    // `page` param is ignored. The first request carries no page param
    // (page 1) and tells us the param name for the rest.
    if (page) url.searchParams.set(pageParam || 'page', String(page));
    const response = await fetch(url.toString(), { credentials: 'same-origin' });
    if (!response.ok) return null;
    // The alternate view serves JSON with a text/html content type.
    return JSON.parse(await response.text());
  }

  #render(brands) {
    const visible = brands.map((brand) => this.#resolve(brand)).filter(Boolean);
    const groups = this.#groupBrands(visible);
    const fragment = document.createDocumentFragment();

    for (const [key, groupBrands] of groups) {
      fragment.appendChild(this.#renderGroup(key, groupBrands));
      this.#enableIndexLetter(key);
    }

    this.groupsContainer.replaceChildren(fragment);
    this.groupsContainer.setAttribute('aria-busy', 'false');
  }

  /**
   * Applies the section's visibility settings to one brand and resolves its
   * final link (VIS-509). Returns `{ name, url }` to show, or null to hide.
   *
   *   - Collection with products: shown, linked to the collection.
   *   - No products to show via its own page — no collection, OR a collection
   *     that's been emptied — both fall through to the same handling:
   *       · "filter brands without products" off  → shown, no link.
   *       · on + "keep brands with vendor products" + vendor has products →
   *         shown; linked to the vendor product filter when "link vendor
   *         filter" is on, otherwise no link.
   *       · otherwise hidden.
   */
  #resolve(brand) {
    if (brand.url && brand.collectionHasProducts) {
      return { name: brand.name, url: brand.url };
    }
    if (!this.filterNoCollection) return { name: brand.name, url: '' };
    if (this.keepWithProducts && brand.hasProducts) {
      const url = this.vendorLink
        ? `/collections/vendors?q=${encodeURIComponent(brand.name)}`
        : '';
      return { name: brand.name, url };
    }
    return null;
  }

  /**
   * Groups brands by first letter into a Map ordered "0-9" first, then A–Z.
   * Sorting happens once globally; groups preserve that order.
   */
  #groupBrands(brands) {
    const seen = new Set();
    const unique = brands.filter((brand) => {
      const name = (brand.name || '').trim();
      if (!name) return false;
      const key = name.toLowerCase();
      if (seen.has(key)) return false;
      seen.add(key);
      return true;
    });

    unique.sort((a, b) => a.name.localeCompare(b.name, 'it', { sensitivity: 'base' }));

    const groups = new Map();
    for (const brand of unique) {
      const key = this.#letterFor(brand.name);
      if (!groups.has(key)) groups.set(key, []);
      groups.get(key).push(brand);
    }

    const orderedKeys = [DIGIT_KEY, ...'ABCDEFGHIJKLMNOPQRSTUVWXYZ'];
    return new Map(
      orderedKeys.filter((key) => groups.has(key)).map((key) => [key, groups.get(key)]),
    );
  }

  #letterFor(name) {
    // Fold diacritics (É → E) before bucketing.
    const first = name
      .trim()
      .normalize('NFD')
      .replace(/\p{M}/gu, '')
      .charAt(0)
      .toUpperCase();
    return /[A-Z]/.test(first) ? first : DIGIT_KEY;
  }

  #renderGroup(key, brands) {
    const group = document.createElement('section');
    group.className = 'brand-list__group';
    group.id = `brand-group-${key}`;

    const heading = document.createElement('h2');
    heading.className = 'brand-list__group-heading';
    heading.textContent = this.#labelFor(key);
    group.appendChild(heading);

    const list = document.createElement('ul');
    list.className = 'brand-list__group-list';
    for (const brand of brands) {
      const item = document.createElement('li');
      item.className = 'brand-list__group-item';
      const link = document.createElement(brand.url ? 'a' : 'span');
      link.className = 'brand-list__group-link';
      if (brand.url) link.href = brand.url;
      link.textContent = brand.name;
      item.appendChild(link);
      list.appendChild(item);
    }
    group.appendChild(list);

    return group;
  }

  #labelFor(key) {
    // The index letters carry the (translated) labels, "0-9" included.
    const letter = this.indexLetters.find((el) => el.dataset.letter === key);
    return letter ? letter.textContent.trim() : key;
  }

  #enableIndexLetter(key) {
    const letter = this.indexLetters.find((el) => el.dataset.letter === key);
    if (!letter) return;
    letter.classList.remove('brand-list__index-letter--empty');
    letter.setAttribute('href', `#brand-group-${key}`);
  }
}

if (!customElements.get('brand-list')) {
  customElements.define('brand-list', BrandList);
}
